import { useNavigate } from '@tanstack/react-router';
import { CalendarClock, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { GoogleLogo } from '~/components/logos/GoogleLogo';
import { MetaLogo } from '~/components/logos/MetaLogo';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import { scheduleWizardRoute } from '../../utils/schedule-run-config';

/** One platform's standing, as the page already knows it for Create New Batch. */
export interface NewSchedulePlatform {
  /** `LIVE` | `TRIAL_POC` | null (not enrolled). */
  contract: string | null;
  /** Connection active and catalog imported. */
  ready: boolean;
}

interface Props {
  google: NewSchedulePlatform;
  meta: NewSchedulePlatform;
}

/**
 * "New schedule": pick the channel, then set the schedule up in the same wizard Create New Batch
 * uses. Routes exactly like batch creation — a channel not connected yet goes to its onboarding,
 * one not enrolled to Product Not Enrolled. Trials are one-off by design, so they get no schedule.
 */
export function NewScheduleMenu({ google, meta }: Props) {
  const { t } = useTranslation('catalog-operations');
  const navigate = useNavigate();

  const item = (
    platform: 'google' | 'meta',
    { contract, ready }: NewSchedulePlatform,
    logo: ReactNode,
    label: string,
  ) => {
    const trial = contract === 'TRIAL_POC';
    const node = (
      <DropdownMenuItem
        data-testid={`catalog-ops-new-schedule-${platform}-item`}
        className={cn(
          'gap-3 px-3 py-2',
          trial ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
        onSelect={(e) => {
          if (trial) e.preventDefault();
        }}
        onClick={() => {
          if (trial) return;
          if (contract === 'LIVE') {
            // Same connector as batch creation; the wizard's route carries the platform.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (ready) void navigate({ to: scheduleWizardRoute(platform) as any });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            else void navigate({ to: `/onboarding/integration/${platform}` as any });
          } else {
            void navigate({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              to: '/product-not-enrolled' as any,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
              search: { product: platform } as any,
            });
          }
        }}
      >
        <span className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
          {logo}
        </span>
        {label}
      </DropdownMenuItem>
    );
    return trial ? (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="left" className="max-w-72 text-center">
          {t('schedules.runWizard.menu.trialTooltip')}
        </TooltipContent>
      </Tooltip>
    ) : (
      node
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="catalog-ops-set-up-schedule-button"
          variant="outline"
          className="cursor-pointer gap-2"
        >
          <CalendarClock className="h-4 w-4" />
          {t('schedules.setUpButton')}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="catalog-ops-new-schedule-menu"
        align="end"
        className="min-w-44"
      >
        {item('google', google, <GoogleLogo className="h-5 w-5" />, 'Google Ads')}
        {item('meta', meta, <MetaLogo className="h-5 w-5" />, 'Meta Ads')}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
